import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import cx from 'clsx';
import { useRef, useState, useEffect, Children } from 'react';
import { u as useFixedPosition } from './hook-CckkA92z.js';
import { u as useLatest } from './hook-BihLfH9C.js';
import { debounce } from '../utils/debounce/index.js';
import { s as styleInject } from './style-inject.es-tgCJW-Cu.js';

const SCROLL_CONTAINER_SCROLL_DEBOUNCE_WAIT_MSEC_DEFAULT = 1;

var css_248z = ".SGCM_UIKIT_REACT_FixedPositionAligner_containerLocate_19a2g_1 {\n  display: contents;\n}\n\n.SGCM_UIKIT_REACT_FixedPositionAligner_containerTarget_19a2g_5 {\n  position: fixed;\n  opacity: 0;\n  visibility: hidden;\n}\n.SGCM_UIKIT_REACT_FixedPositionAligner_containerTarget_19a2g_5.SGCM_UIKIT_REACT_FixedPositionAligner_isInitialized_19a2g_10 {\n  opacity: unset;\n  visibility: unset;\n}\n/*# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy5tb2R1bGUuc2NzcyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQTtFQUNFLGlCQUFpQjtBQUNuQjs7QUFFQTtFQUNFLGVBQWU7RUFDZixVQUFVO0VBQ1Ysa0JBQWtCO0FBQ3BCO0FBQ0E7RUFDRSxjQUFjO0VBQ2QsaUJBQWlCO0FBQ25CIiwiZmlsZSI6InN0eWxlcy5tb2R1bGUuc2NzcyIsInNvdXJjZXNDb250ZW50IjpbIi5jb250YWluZXJMb2NhdGUge1xuICBkaXNwbGF5OiBjb250ZW50cztcbn1cblxuLmNvbnRhaW5lclRhcmdldCB7XG4gIHBvc2l0aW9uOiBmaXhlZDtcbiAgb3BhY2l0eTogMDtcbiAgdmlzaWJpbGl0eTogaGlkZGVuO1xufVxuLmNvbnRhaW5lclRhcmdldC5pc0luaXRpYWxpemVkIHtcbiAgb3BhY2l0eTogdW5zZXQ7XG4gIHZpc2liaWxpdHk6IHVuc2V0O1xufSJdfQ== */";
var styles = {"containerLocate":"SGCM_UIKIT_REACT_FixedPositionAligner_containerLocate_19a2g_1","containerTarget":"SGCM_UIKIT_REACT_FixedPositionAligner_containerTarget_19a2g_5","isInitialized":"SGCM_UIKIT_REACT_FixedPositionAligner_isInitialized_19a2g_10"};
styleInject(css_248z);

const FixedPositionAligner = ({ children, className, isActive, offset, rectCalculator, scrollContainerScrollDebounceWaitMsec = (SCROLL_CONTAINER_SCROLL_DEBOUNCE_WAIT_MSEC_DEFAULT), targetElement, targetElementClassName, autoLocate = true, onLocateElementWidthChange, }) => {
    const locateContainerElementRef = useRef(null);
    const locateElementRef = useRef(null);
    const targetElementRef = useRef(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const { rect, refreshRect } = useFixedPosition({
        locateElementRef,
        targetElementRef,
        rectCalculator,
    });
    const { ref: onLatestLocateElementWidthChangeRef } = useLatest({
        value: onLocateElementWidthChange,
    });
    useEffect(() => {
        if (!isActive) {
            setIsInitialized(false);
            return;
        }
        if (isInitialized) {
            return;
        }
        const locateElement = locateContainerElementRef.current
            ?.firstElementChild;
        if (!locateElement) {
            return;
        }
        locateElementRef.current = locateElement;
        refreshRect();
        setIsInitialized(true);
    }, [
        isActive,
        isInitialized,
        refreshRect,
    ]);
    useEffect(() => {
        if (!isInitialized) {
            return undefined;
        }
        const { current: locateElement } = locateElementRef;
        if (!locateElement) {
            return undefined;
        }
        const observer = new ResizeObserver(([{ contentRect: { width, }, },]) => {
            onLatestLocateElementWidthChangeRef.current?.(width);
        });
        observer.observe(locateElement);
        onLatestLocateElementWidthChangeRef.current?.(locateElement.clientWidth);
        return () => {
            observer.disconnect();
        };
    }, [
        isInitialized,
        onLatestLocateElementWidthChangeRef,
    ]);
    useEffect(() => {
        if (!isInitialized) {
            return undefined;
        }
        const { requestAnimationFrame, cancelAnimationFrame } = window;
        const onDocScroll = debounce(() => {
            const reqAnimId = requestAnimationFrame(() => {
                refreshRect();
                cancelAnimationFrame(reqAnimId);
            });
        }, scrollContainerScrollDebounceWaitMsec);
        document.addEventListener('scroll', onDocScroll);
        return () => {
            document.removeEventListener('scroll', onDocScroll);
        };
    }, [
        isInitialized,
        refreshRect,
        scrollContainerScrollDebounceWaitMsec,
    ]);
    if (Children.count(children) !== 1) {
        throw new TypeError('Number of children should be one');
    }
    if (!isActive) {
        return children;
    }
    return (jsxs(Fragment, { children: [jsx("div", { ref: locateContainerElementRef, className: cx(styles.containerLocate, className), children: children }), jsx("div", { ref: targetElementRef, className: cx(styles.containerTarget, {
                    [styles.isInitialized]: isInitialized,
                }, targetElementClassName), style: rect
                    ? {
                        left: autoLocate
                            ? rect.left + (offset?.left ?? 0)
                            : (offset?.left ?? 0),
                        top: autoLocate
                            ? rect.top + (offset?.top ?? 0)
                            : (offset?.top ?? 0),
                    }
                    : undefined, children: targetElement })] }));
};

export { FixedPositionAligner as F };
//# sourceMappingURL=component-gOY2cmyW.js.map
